import { format, isWithinInterval, areIntervalsOverlapping, differenceInDays } from 'date-fns';
import { fr } from 'date-fns/locale';

/**
 * Service pour vérifier la conformité d'une semaine spécifique
 */
export class WeekComplianceService {
  
  /**
   * Vérifie la conformité d'une semaine spécifique
   * @param {Object} weekData - Données de la semaine
   * @param {Array} onCalls - Toutes les astreintes
   * @param {Object} technicians - Map des techniciens
   * @param {Array} skills - Liste des compétences
   * @param {Array} absences - Liste des absences
   * @param {Array} holidays - Jours fériés
   * @param {Array} allOnCalls - Toutes les astreintes (pour vérification repos)
   * @returns {Object} Résultat de conformité pour cette semaine
   */
  static async checkWeekCompliance(weekData, onCalls, technicians, skills, absences, holidays, allOnCalls = []) {
    const result = {
      weekNumber: weekData.weekNumber,
      weekPeriod: `${format(weekData.startDate, 'dd/MM', { locale: fr })} au ${format(weekData.endDate, 'dd/MM', { locale: fr })}`,
      status: 'valid', // 'valid', 'warning', 'error'
      conflicts: [],
      warnings: [],
      summary: {
        totalIssues: 0,
        criticalIssues: 0,
        warningIssues: 0
      }
    };

    try {
      // Filtrer les astreintes pour cette semaine
      const weekOnCalls = onCalls.filter(onCall => {
        const onCallStart = new Date(onCall.startDate);
        const onCallEnd = new Date(onCall.endDate);
        
        return isWithinInterval(weekData.startDate, { start: onCallStart, end: onCallEnd }) ||
               isWithinInterval(weekData.endDate, { start: onCallStart, end: onCallEnd }) ||
               isWithinInterval(onCallStart, { start: weekData.startDate, end: weekData.endDate });
      });

      // 1. Vérifier les conflits d'absences
      await this.checkWeekAbsenceConflicts(weekData, weekOnCalls, absences, result);

      // 2. Vérifier les conflits de compétences
      this.checkWeekSkillConflicts(weekData, weekOnCalls, skills, result);

      // 3. Vérifier la couverture des compétences
      this.checkWeekSkillCoverage(weekData, weekOnCalls, skills, result);

      // 4. Vérifier les périodes de repos (pour les astreintes de cette semaine)
      this.checkWeekRestPeriods(weekData, weekOnCalls, allOnCalls, technicians, result);

      // Déterminer le statut global
      if (result.conflicts.length > 0) {
        result.status = 'error';
      } else if (result.warnings.length > 0) {
        result.status = 'warning';
      } else {
        result.status = 'valid';
      }

      result.summary.totalIssues = result.conflicts.length + result.warnings.length;
      result.summary.criticalIssues = result.conflicts.length;
      result.summary.warningIssues = result.warnings.length;

    } catch (error) {
      console.error('Erreur lors de la vérification de conformité de la semaine:', error);
      result.status = 'error';
      result.conflicts.push({
        type: 'SYSTEM_ERROR',
        severity: 'critical',
        message: 'Erreur système lors de la vérification',
        details: error.message
      });
    }

    return result;
  }

  /**
   * Vérifie les conflits d'absences pour une semaine
   */
  static async checkWeekAbsenceConflicts(weekData, weekOnCalls, absences, result) {
    // Grouper les conflits par technicien pour mutualiser
    const conflictsByTechnician = {};
    
    for (const onCall of weekOnCalls) {
      const technicianAbsences = absences.filter(abs => abs.technicianId === onCall.technicianId);
      
      if (technicianAbsences.length === 0) continue;
      
      // Filtrer les absences qui chevauchent avec l'astreinte (en excluant celles avec bypass)
      const conflictingAbsences = technicianAbsences.filter(absence => {
        // Ignorer les absences avec bypass activé
        if (absence.bypassAbsenceValidation) {
          return false;
        }
        const absenceStart = new Date(absence.startDate);
        const absenceEnd = new Date(absence.endDate);
        const onCallStart = new Date(onCall.startDate);
        const onCallEnd = new Date(onCall.endDate);

        // Créer les dates avec fuseau horaire explicite pour éviter les décalages UTC
        const onCallStartTime = new Date(onCallStart.toISOString().split('T')[0] + 'T00:00:00');
        const onCallEndTime = new Date(onCallEnd.toISOString().split('T')[0] + 'T23:59:59');
        const absenceStartTime = new Date(absenceStart.toISOString().split('T')[0] + 'T00:00:00');
        const absenceEndTime = new Date(absenceEnd.toISOString().split('T')[0] + 'T23:59:59');

        return areIntervalsOverlapping(
          { start: onCallStartTime, end: onCallEndTime },
          { start: absenceStartTime, end: absenceEndTime },
          { inclusive: true }
        );
      });

      if (conflictingAbsences.length > 0) {
        if (!conflictsByTechnician[onCall.technicianId]) {
          conflictsByTechnician[onCall.technicianId] = {
            technicianId: onCall.technicianId,
            onCallId: onCall.id,
            bypassActivated: false, // Les absences avec bypass sont déjà filtrées
            absences: []
          };
        }
        
        // Ajouter les absences sans doublon
        conflictingAbsences.forEach(absence => {
          if (!conflictsByTechnician[onCall.technicianId].absences.find(a => a.id === absence.id)) {
            conflictsByTechnician[onCall.technicianId].absences.push(absence);
          }
        });
      }
    }

    // Créer un conflit mutualisé par technicien
    Object.values(conflictsByTechnician).forEach(techConflict => {
      const { severity, message } = this.getGroupedAbsenceSeverityAndMessage(techConflict.absences, techConflict.bypassActivated);
      
      const conflictData = {
        type: 'ABSENCE_CONFLICT_GROUP',
        severity: severity,
        message: message,
        details: this.formatGroupedAbsenceDetails(techConflict.absences),
        technicianId: techConflict.technicianId,
        onCallId: techConflict.onCallId,
        absenceIds: techConflict.absences.map(a => a.id),
        weekStart: weekData.startDate,
        absences: techConflict.absences,
        bypassActivated: techConflict.bypassActivated
      };

      if (severity === 'critical') {
        result.conflicts.push(conflictData);
      } else {
        result.warnings.push(conflictData);
      }
    });
  }

  /**
   * Vérifie les conflits de compétences pour une semaine
   */
  static checkWeekSkillConflicts(weekData, weekOnCalls, skills, result) {
    const skillAssignments = {};
    
    weekOnCalls.forEach(onCall => {
      onCall.skills.forEach(skillId => {
        if (!skillAssignments[skillId]) {
          skillAssignments[skillId] = [];
        }
        skillAssignments[skillId].push(onCall);
      });
    });

    Object.entries(skillAssignments).forEach(([skillId, assignments]) => {
      if (assignments.length > 1) {
        const skill = skills.find(s => s.id === parseInt(skillId));
        result.conflicts.push({
          type: 'SKILL_CONFLICT',
          severity: 'critical',
          message: `Conflit sur la compétence ${skill?.name || skillId}`,
          details: `${assignments.length} techniciens assignés simultanément`,
          skillId: parseInt(skillId),
          weekStart: weekData.startDate,
          onCalls: assignments.map(oc => oc.id),
          onCallId: assignments[0].id
        });
      }
    });
  }

  /**
   * Vérifie la couverture des compétences pour une semaine
   */
  static checkWeekSkillCoverage(weekData, weekOnCalls, skills, result) {
    const coveredSkills = new Set();
    
    console.log('🔍 checkWeekSkillCoverage - Vérification semaine:', {
      weekNumber: weekData.weekNumber,
      weekPeriod: `${format(weekData.startDate, 'dd/MM/yyyy')} - ${format(weekData.endDate, 'dd/MM/yyyy')}`,
      totalOnCalls: weekOnCalls.length,
      totalSkillsToCheck: skills.length
    });
    
    weekOnCalls.forEach(onCall => {
      // 🔧 CORRECTION : Ajouter une vérification pour onCall.skills
      const onCallSkills = onCall.skills || [];
      console.log(`  - Astreinte ${onCall.id}: ${onCallSkills.length} compétences`, onCallSkills);
      
      onCallSkills.forEach(skillId => {
        coveredSkills.add(skillId);
      });
    });

    console.log('  ✅ Compétences couvertes:', Array.from(coveredSkills));
    console.log('  📋 Compétences à vérifier:', skills.map(s => ({ id: s.id, name: s.name })));

    const uncoveredSkills = skills.filter(skill => !coveredSkills.has(skill.id));
    
    if (uncoveredSkills.length > 0) {
      console.log('  ⚠️ Compétences NON couvertes:', uncoveredSkills.map(s => ({ id: s.id, name: s.name })));
      
      result.warnings.push({
        type: 'COVERAGE_GAP',
        severity: 'warning',
        message: `Compétences non couvertes: ${uncoveredSkills.map(s => s.name).join(', ')}`,
        details: `${uncoveredSkills.length} compétence(s) sans technicien assigné`,
        weekStart: weekData.startDate,
        uncoveredSkills: uncoveredSkills.map(s => s.id)
      });
    } else {
      console.log('  ✅ Toutes les compétences sont couvertes');
    }
  }

  /**
   * Vérifie les périodes de repos pour les astreintes de cette semaine
   */
  static checkWeekRestPeriods(weekData, weekOnCalls, allOnCalls, technicians, result) {
    for (const onCall of weekOnCalls) {
      const technicianOnCalls = allOnCalls
        .filter(oc => oc.technicianId === onCall.technicianId)
        .sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
      
      const currentIndex = technicianOnCalls.findIndex(oc => oc.id === onCall.id);
      
      if (currentIndex > 0) {
        const previousOnCall = technicianOnCalls[currentIndex - 1];
        const previousEndDate = new Date(previousOnCall.endDate);
        const currentStartDate = new Date(onCall.startDate);
        
        const daysBetween = differenceInDays(currentStartDate, previousEndDate);
        
        if (daysBetween < 14) {
          const technician = technicians[onCall.technicianId];
          const technicianName = technician ? `${technician.firstName} ${technician.lastName}` : `Technicien ${onCall.technicianId}`;
          
          result.conflicts.push({
            type: 'REST_PERIOD_VIOLATION',
            severity: 'critical',
            message: `Période de repos insuffisante pour ${technicianName}`,
            details: `Seulement ${daysBetween} jour(s) depuis la précédente astreinte (minimum requis: 14 jours)`,
            technicianId: onCall.technicianId,
            currentOnCallId: onCall.id,
            nextOnCallId: previousOnCall.id,
            currentEndDate: format(previousEndDate, 'dd/MM/yyyy', { locale: fr }),
            nextStartDate: format(currentStartDate, 'dd/MM/yyyy', { locale: fr }),
            daysBetween: daysBetween
          });
        }
      }
    }
  }

  /**
   * Retourne le label d'affichage pour un type d'absence
   */
  static getAbsenceDisplayLabel(absenceType) {
    const ABSENCE_TYPES = {
      CP: { label: 'CA' },
      JTL: { label: 'JTL' },
      MALADIE: { label: 'Arrêt maladie' },
      FORMATION: { label: 'Formation' },
      CM: { label: 'CM' },
      TPS: { label: 'Temps partiel' },
      RECUP: { label: 'Récupération' },
      RECUP_ASTREINTE: { label: 'Récup astreinte' },
      MEDICAL: { label: 'Visite médicale' },
      AUTRE: { label: 'Autre' }
    };

    if (!absenceType) return 'Type inconnu';
    const normalizedType = absenceType.toUpperCase();
    return ABSENCE_TYPES[normalizedType]?.label || absenceType;
  }

  /**
   * Retourne la sévérité et le message pour un type d'absence
   */
  static getAbsenceSeverityAndMessage(absenceType) {
    const type = absenceType?.toUpperCase();
    const displayLabel = this.getAbsenceDisplayLabel(absenceType);

    switch (type) {
      case 'JTL':
        return { severity: 'warning', message: `Conflit avec absence ${displayLabel} (compatible mais à noter)` };
      case 'RECUP_ASTREINTE':
        return { severity: 'info', message: `${displayLabel} détectée (information seulement, non bloquante)` };
      case 'CA':
      case 'CP':
        return { severity: 'critical', message: `Conflit avec ${displayLabel} - BLOQUANT` };
      case 'ARRET MALADIE':
      case 'MALADIE':
        return { severity: 'critical', message: `Conflit avec ${displayLabel} - BLOQUANT` };
      case 'FORMATION':
        return { severity: 'critical', message: `Conflit avec ${displayLabel} - BLOQUANT` };
      case 'CM':
        return { severity: 'critical', message: `Conflit avec ${displayLabel} - BLOQUANT` };
      case 'RECUP':
        return { severity: 'critical', message: `Conflit avec ${displayLabel} - BLOQUANT` };
      case 'MEDICAL':
        return { severity: 'critical', message: `Conflit avec ${displayLabel} - BLOQUANT` };
      case 'AUTRE':
        return { severity: 'critical', message: `Absence de type "${displayLabel}" - Veuillez spécifier le type d'absence` };
      case 'TPS':
        return { severity: 'critical', message: `Conflit avec ${displayLabel} - BLOQUANT` };
      default:
        return { severity: 'critical', message: `Conflit avec absence ${displayLabel} - À vérifier` };
    }
  }

  /**
   * Retourne la sévérité et le message pour un groupe d'absences d'un technicien
   */
  static getGroupedAbsenceSeverityAndMessage(absences, bypassActivated) {
    // Séparer les types d'absences
    const criticalTypes = [];
    const warningTypes = [];
    
    absences.forEach(absence => {
      const { severity } = this.getAbsenceSeverityAndMessage(absence.type);
      if (severity === 'critical') {
        criticalTypes.push(this.getAbsenceDisplayLabel(absence.type));
      } else {
        warningTypes.push(this.getAbsenceDisplayLabel(absence.type));
      }
    });

    // Supprimer les doublons et trier
    const uniqueCritical = [...new Set(criticalTypes)].sort();
    const uniqueWarning = [...new Set(warningTypes)].sort();

    // Déterminer la sévérité finale
    const hasCritical = uniqueCritical.length > 0;
    const finalSeverity = bypassActivated ? 'warning' : (hasCritical ? 'critical' : 'warning');

    // Construire le message
    let message = '';
    if (hasCritical && uniqueWarning.length > 0) {
      // Mélange critique et avertissement
      message = `Conflits multiples: ${uniqueCritical.join(', ')} (BLOQUANT) et ${uniqueWarning.join(', ')} (Compatible)`;
    } else if (hasCritical) {
      // Seulement critique
      message = uniqueCritical.length === 1 
        ? `Conflit avec ${uniqueCritical[0]} - BLOQUANT`
        : `Conflits multiples: ${uniqueCritical.join(', ')} - BLOQUANT`;
    } else {
      // Seulement avertissement
      message = uniqueWarning.length === 1
        ? `Conflit avec absence ${uniqueWarning[0]} (compatible mais à noter)`
        : `Conflits multiples: ${uniqueWarning.join(', ')} (compatibles mais à noter)`;
    }

    if (bypassActivated) {
      message += ' (BYPASS ACTIVÉ - Accord manager)';
    }

    return { severity: finalSeverity, message };
  }

  /**
   * Formate les détails pour un groupe d'absences
   */
  static formatGroupedAbsenceDetails(absences) {
    if (absences.length === 1) {
      const absence = absences[0];
      return `Technicien en absence du ${format(new Date(absence.startDate), 'dd/MM/yyyy', { locale: fr })} au ${format(new Date(absence.endDate), 'dd/MM/yyyy', { locale: fr })}`;
    }

    const dateRanges = absences.map(absence => 
      `${format(new Date(absence.startDate), 'dd/MM/yyyy', { locale: fr })} au ${format(new Date(absence.endDate), 'dd/MM/yyyy', { locale: fr })}`
    );
    
    return `Technicien en absence les: ${dateRanges.join(', ')}`;
  }
}

export default WeekComplianceService;
